// Shared building blocks for the feature screens (task 3.6).
//
// The feature screens (clients, jobs, quotes, invoices, settings) all speak to the
// Phase 2 API through ./api.js and render with the shell's shared loading / error /
// empty states (views.js). This module holds the small, framework-free UI
// primitives they reuse — screen scaffold, form fields, money formatting, list
// rows, status badges and the repeatable line-item editor — so no screen reinvents
// them. It builds on ../lib/dom.js (h/clear/mount) and stays dependency-free.
//
// The pure, non-DOM helpers (money formatting, number parsing, packing a job's
// composite id, the status-transition maps) are unit-tested in ui.test.js; the
// DOM builders are exercised end-to-end in the browser.

import { h, clear, mount } from '../lib/dom.js';
import { routeToHash } from '../router.js';
import { runAsync, emptyView } from '../views.js';

export { h, clear, mount, routeToHash, runAsync, emptyView };

// --- Money -----------------------------------------------------------------

// The API is the source of truth for money (computed server-side in integer
// pence — NFR6.2). These formatters are display-only. Prefer the *Pence figure
// when the API provides it so what we show matches what was stored exactly.
const GBP = new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' });

export function moneyFromPence(pence) {
  const n = Number.isFinite(pence) ? pence : 0;
  return GBP.format(n / 100);
}

export function moneyFromPounds(pounds) {
  const n = Number.isFinite(pounds) ? pounds : 0;
  return GBP.format(n);
}

// Parse a form number field to a finite number, treating blank/invalid as 0.
export function toNum(value) {
  const n = parseFloat(value);
  return Number.isFinite(n) ? n : 0;
}

// A short, human date from an ISO timestamp (job/quote/invoice updatedAt).
export function shortDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

// --- Job composite id ------------------------------------------------------

// A job is addressed by its client (partition) AND its own id, but the router
// carries a single id segment. We pack both into one route id and unpack it in the
// job detail screen. UUIDs (newId) never contain '~', so it's a safe separator;
// the router percent-encodes the segment, so the '~' round-trips cleanly.
const JOB_ID_SEP = '~';

export function packJobId(clientId, jobId) {
  return `${clientId}${JOB_ID_SEP}${jobId}`;
}

export function unpackJobId(packed) {
  const raw = String(packed || '');
  const i = raw.indexOf(JOB_ID_SEP);
  if (i <= 0 || i >= raw.length - 1) return null;
  return { clientId: raw.slice(0, i), jobId: raw.slice(i + 1) };
}

// --- Domain mirrors (kept in step with api/lib) ----------------------------

// Job lifecycle (api/lib/jobs.js JOB_STATUSES).
export const JOB_STATUSES = ['quoted', 'scheduled', 'in progress', 'completed', 'invoiced'];
export const DEFAULT_JOB_STATUS = 'scheduled';

// Certificate types Sparky records against a job (api/lib/jobs.js).
export const REQUIRED_CERTIFICATES = ['EIC', 'MEIWC', 'EICR'];

// Allowed status transitions, mirroring api/lib/quotes.js + api/lib/invoices.js so
// the UI only offers moves the API will accept (it still enforces them). The
// labels are the button text for each target status.
export const QUOTE_TRANSITIONS = {
  draft: ['sent', 'expired', 'rejected'],
  sent: ['accepted', 'rejected', 'expired'],
  accepted: [],
  rejected: [],
  expired: [],
};

export const INVOICE_TRANSITIONS = {
  draft: ['pushed-to-QuickBooks', 'sent', 'paid'],
  'pushed-to-QuickBooks': ['sent', 'paid'],
  sent: ['paid'],
  paid: [],
};

export const STATUS_ACTION_LABELS = {
  sent: 'Mark as sent',
  accepted: 'Mark accepted',
  rejected: 'Mark rejected',
  expired: 'Mark expired',
  'pushed-to-QuickBooks': 'Mark pushed to QuickBooks',
  paid: 'Mark paid',
};

// --- Element id helper -----------------------------------------------------

let _uid = 0;
export function uid(prefix = 'f') {
  _uid += 1;
  return `${prefix}-${_uid}`;
}

// --- Screen scaffold -------------------------------------------------------

// A screen: an optional back link, a title row (with optional header actions), and
// the body. Matches the shell's own `screen()` markup (`.screen` / `.screen-title`)
// so feature screens sit in the same layout the shell provides.
export function screenEl(title, children, { section, backHref, backLabel = 'Back', actions } = {}) {
  const parts = [];
  if (backHref) {
    parts.push(h('a', { class: 'back-link', href: backHref }, [`‹ ${backLabel}`]));
  }
  parts.push(
    h('div', { class: 'screen-head' }, [
      h('h1', { class: 'screen-title', text: title }),
      actions ? h('div', { class: 'screen-head-actions' }, [].concat(actions)) : null,
    ]),
  );
  parts.push(...[].concat(children).filter(Boolean));
  return h('section', { class: 'screen', 'data-section': section }, parts);
}

// --- Buttons / links -------------------------------------------------------

export function button(label, { variant = 'secondary', small = false, ...attrs } = {}) {
  const cls = `btn btn-${variant}${small ? ' btn-sm' : ''}`;
  return h('button', { class: cls, type: 'button', ...attrs }, [label]);
}

export function linkButton(label, href, { variant = 'secondary', small = false } = {}) {
  const cls = `btn btn-${variant}${small ? ' btn-sm' : ''}`;
  return h('a', { class: cls, href }, [label]);
}

// A row of buttons/links (screen actions, form actions).
export function buttonRow(children) {
  return h('div', { class: 'button-row' }, [].concat(children).filter(Boolean));
}

// --- Form fields -----------------------------------------------------------

export function makeInput({ type = 'text', value = '', ...attrs } = {}) {
  return h('input', { class: 'input', type, value: value == null ? '' : String(value), ...attrs });
}

export function makeTextarea({ value = '', rows = 3, ...attrs } = {}) {
  const el = h('textarea', { class: 'input textarea', rows: String(rows), ...attrs });
  el.value = value == null ? '' : String(value);
  return el;
}

// options: [{ value, label }]. `value` selects the matching option.
export function makeSelect(options, { value, ...attrs } = {}) {
  const el = h(
    'select',
    { class: 'input select', ...attrs },
    options.map((o) =>
      h('option', { value: o.value, selected: o.value === value ? true : null }, [o.label]),
    ),
  );
  if (value != null) el.value = value;
  return el;
}

export function makeCheckbox({ checked = false, ...attrs } = {}) {
  const el = h('input', { class: 'checkbox', type: 'checkbox', ...attrs });
  el.checked = !!checked;
  return el;
}

// Wrap a control with a label (associated via id/for) and an optional hint.
export function field(labelText, control, { hint } = {}) {
  const id = control.id || uid('f');
  control.id = id;
  return h('div', { class: 'field' }, [
    h('label', { class: 'field-label', for: id, text: labelText }),
    control,
    hint ? h('p', { class: 'field-hint', text: hint }) : null,
  ]);
}

// A checkbox laid out inline with its label.
export function checkboxField(labelText, control, { hint } = {}) {
  const id = control.id || uid('c');
  control.id = id;
  return h('div', { class: 'checkbox-field' }, [
    control,
    h('label', { class: 'checkbox-label', for: id, text: labelText }),
    hint ? h('p', { class: 'field-hint', text: hint }) : null,
  ]);
}

// An inline error line; empty by default (hidden via `.form-error:empty` CSS).
export function errorSlot() {
  return h('p', { class: 'form-error', role: 'alert' });
}

export function setError(slot, message) {
  if (slot) slot.textContent = message || '';
}

// --- Content blocks --------------------------------------------------------

export function card(children, { title } = {}) {
  const parts = [];
  if (title) parts.push(h('h2', { class: 'card-title', text: title }));
  parts.push(...[].concat(children).filter(Boolean));
  return h('div', { class: 'card' }, parts);
}

export function sectionHeading(text) {
  return h('h2', { class: 'section-heading', text });
}

export function muted(text) {
  return h('p', { class: 'muted', text });
}

export function statusBadge(status) {
  const slug = String(status || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
  return h('span', { class: `badge badge-${slug}`, text: status });
}

// A tappable list row linking to `href`.
export function listRow({ href, title, subtitle, meta }) {
  return h('a', { class: 'list-row', href }, [
    h('div', { class: 'list-row-main' }, [
      h('span', { class: 'list-row-title', text: title }),
      subtitle ? h('span', { class: 'list-row-sub', text: subtitle }) : null,
    ]),
    meta ? h('span', { class: 'list-row-meta' }, [].concat(meta)) : null,
  ]);
}

// A read-only label/value list for detail screens. `pairs` is [[label, value], …]
// where value is a string or a node; falsy pairs and null values are skipped.
export function detailList(pairs) {
  const rows = pairs
    .filter((p) => p && p[1] != null && p[1] !== '')
    .map(([label, value]) =>
      h('div', { class: 'detail-row' }, [
        h('span', { class: 'detail-label', text: label }),
        h('span', { class: 'detail-value' }, [value]),
      ]),
    );
  return h('div', { class: 'detail-list' }, rows);
}

// --- Navigation ------------------------------------------------------------

// Programmatic navigation (after a successful create/redirect). Setting the hash
// fires `hashchange`, which the shell's router turns into a re-render.
export function goTo(route) {
  window.location.hash = typeof route === 'string' ? route : routeToHash(route);
}

// --- Action runner ---------------------------------------------------------

// Run an async mutation from a button press with consistent busy + error UX:
// disable the button and swap its label while in flight, show the message in
// `errSlot` on failure, and restore the button. Returns the task's result (or
// undefined on failure).
export async function runAction(btn, task, { busyLabel = 'Working…', errSlot } = {}) {
  const original = btn.textContent;
  btn.disabled = true;
  btn.textContent = busyLabel;
  setError(errSlot, '');
  try {
    return await task();
  } catch (err) {
    setError(errSlot, (err && err.message) || 'Something went wrong. Please try again.');
    return undefined;
  } finally {
    btn.disabled = false;
    btn.textContent = original;
  }
}

// --- Repeatable rows -------------------------------------------------------

// A managed list of add/removable rows (materials, labour, sites, quote lines).
// `makeRow(data, remove, notify)` builds a row and returns { el, read }, where
// read() returns the row's value (or null to skip it). Returns the wrapper plus
// value/add accessors.
export function repeatableList({ initial = [], makeRow, addLabel = 'Add', onChange = () => {} }) {
  const rows = [];
  const listEl = h('div', { class: 'repeat-list' });

  const addRow = (data) => {
    const holder = {};
    const remove = () => {
      const idx = rows.indexOf(holder);
      if (idx >= 0) rows.splice(idx, 1);
      if (holder.el.parentNode === listEl) listEl.removeChild(holder.el);
      onChange();
    };
    const { el, read } = makeRow(data || {}, remove, onChange);
    holder.el = el;
    holder.read = read;
    rows.push(holder);
    listEl.appendChild(el);
    onChange();
    return holder;
  };

  initial.forEach(addRow);

  const addBtn = h('button', { class: 'btn btn-secondary btn-sm', type: 'button', onclick: () => addRow() }, [addLabel]);
  const el = h('div', { class: 'repeatable' }, [listEl, addBtn]);

  return {
    el,
    values: () => rows.map((r) => r.read()).filter((v) => v != null),
    add: addRow,
  };
}

// A priced line-item editor for quotes and invoices. Rows are description /
// quantity / unit price; a live subtotal PREVIEW is shown (the server computes the
// authoritative figures on save — NFR6.2). Returns { el, lines, add }.
export function linesEditor({ initial = [] } = {}) {
  const subtotalEl = h('span', { class: 'lines-subtotal-value' });

  let list;
  const recompute = () => {
    if (!list) return;
    const sum = list.values().reduce((acc, l) => acc + l.quantity * l.unitPrice, 0);
    subtotalEl.textContent = moneyFromPounds(sum);
  };

  const makeRow = (data, remove, notify) => {
    const desc = makeInput({ type: 'text', value: data.description || '', placeholder: 'Description' });
    desc.classList.add('line-desc');
    const qty = makeInput({
      type: 'number', value: data.quantity ?? '', placeholder: 'Qty', inputmode: 'decimal', step: 'any', min: '0',
    });
    qty.classList.add('line-qty');
    const price = makeInput({
      type: 'number', value: data.unitPrice ?? '', placeholder: 'Unit £', inputmode: 'decimal', step: 'any',
    });
    price.classList.add('line-price');
    const total = h('span', { class: 'line-total' });

    const update = () => {
      total.textContent = moneyFromPounds(toNum(qty.value) * toNum(price.value));
      notify();
    };
    desc.addEventListener('input', notify);
    qty.addEventListener('input', update);
    price.addEventListener('input', update);

    const removeBtn = h('button', { class: 'icon-remove', type: 'button', 'aria-label': 'Remove line', onclick: remove }, ['×']);
    const el = h('div', { class: 'line-row' }, [
      desc,
      h('div', { class: 'line-nums' }, [qty, price, total]),
      removeBtn,
    ]);
    update();

    const read = () => {
      const description = desc.value.trim();
      if (!description) return null; // skip blank rows on submit
      return { description, quantity: toNum(qty.value), unitPrice: toNum(price.value) };
    };
    return { el, read };
  };

  list = repeatableList({
    initial: initial.length ? initial : [{}],
    makeRow,
    addLabel: 'Add line',
    onChange: recompute,
  });
  recompute();

  const el = h('div', { class: 'lines-editor' }, [
    h('div', { class: 'lines-head' }, [
      h('span', { text: 'Item' }),
      h('span', { text: 'Qty · Unit £ · Total' }),
    ]),
    list.el,
    h('div', { class: 'lines-subtotal' }, [
      h('span', { class: 'muted', text: 'Subtotal (preview)' }),
      subtotalEl,
    ]),
  ]);

  return { el, lines: () => list.values(), add: (data) => list.add(data) };
}

// Render read-only quote/invoice lines + totals from an API record (server
// figures). `doc` has lines[] with lineTotalPence, plus subtotalPence, vatApplied,
// vatPence, totalPence.
export function totalsBlock(doc) {
  const lineRows = (doc.lines || []).map((l) =>
    h('div', { class: 'doc-line' }, [
      h('span', { class: 'doc-line-desc', text: l.description }),
      h('span', { class: 'doc-line-qty', text: `${l.quantity} × ${moneyFromPounds(l.unitPrice)}` }),
      h('span', { class: 'doc-line-total', text: moneyFromPence(l.lineTotalPence) }),
    ]),
  );

  const totals = [
    h('div', { class: 'totals-row' }, [
      h('span', { text: 'Subtotal' }),
      h('span', { text: moneyFromPence(doc.subtotalPence) }),
    ]),
  ];
  if (doc.vatApplied) {
    totals.push(
      h('div', { class: 'totals-row' }, [
        h('span', { text: 'VAT' }),
        h('span', { text: moneyFromPence(doc.vatPence) }),
      ]),
    );
  }
  totals.push(
    h('div', { class: 'totals-row totals-grand' }, [
      h('span', { text: 'Total' }),
      h('span', { text: moneyFromPence(doc.totalPence) }),
    ]),
  );

  return h('div', { class: 'doc-lines' }, [...lineRows, h('div', { class: 'totals' }, totals)]);
}

// ===========================================================================
// AI drafting + guideline review UI (task 4.7)
// ===========================================================================
//
// The Phase 4 endpoints draft quotes/invoices and review jobs against the
// in-house guideline checklist. Everything the AI produces is DECISION SUPPORT
// the electrician must review and explicitly confirm — nothing is sent or pushed
// automatically (NFR6.1, Req 4.7/6.3). These helpers render that draft/review
// experience: pure text/mapping helpers (unit-tested), plus the DOM building
// blocks (draft line editor, badges, disclaimer) the quote/invoice/job screens
// reuse so the AI surfaces look and behave the same everywhere.

// --- Draft seed (carry a client/job into the AI form across a route change) --

// The router only carries a single id segment, so a "Draft with AI from this
// job" action can't pass the client/job/description through the hash. Instead the
// origin screen stashes a one-shot seed here and the New quote / New invoice
// screen consumes it once on load. Cleared on read so a later manual visit starts
// blank.
let _draftSeed = null;

export function setDraftSeed(seed) {
  _draftSeed = seed && typeof seed === 'object' ? seed : null;
}

// Return and clear the pending seed IF it targets `section` ('quotes'|'invoices').
export function takeDraftSeed(section) {
  if (_draftSeed && _draftSeed.section === section) {
    const seed = _draftSeed;
    _draftSeed = null;
    return seed;
  }
  return null;
}

// ===========================================================================
// QuickBooks helpers (task 5.4) — pure, so they live here (no api/Cognito import)
// and are unit-tested in ui.test.js. The DOM/api-bound QuickBooks UI (the push
// card + Settings panel) lives in screens/quickbooks.js and re-exports these.
// ===========================================================================

// --- OAuth return (/settings?quickbooks=connected|error) -------------------

// The OAuth callback can't carry a hash, so it redirects the browser to
// `${APP_ORIGIN}/settings?quickbooks=connected|error`. app.js reads that query on
// boot and stashes the outcome here as a one-shot the Settings panel consumes to
// show a banner (mirrors the draft-seed pattern above).
let _qbReturnStatus = null;

// Pure: pull the `quickbooks` outcome out of a location.search string, or null.
export function parseQuickBooksReturn(search) {
  if (typeof search !== 'string' || search === '') return null;
  const query = search.charAt(0) === '?' ? search.slice(1) : search;
  const value = new URLSearchParams(query).get('quickbooks');
  return value === 'connected' || value === 'error' ? value : null;
}

export function setQuickBooksReturnStatus(status) {
  _qbReturnStatus = status === 'connected' || status === 'error' ? status : null;
}

// Return and clear the pending banner outcome (one-shot).
export function takeQuickBooksReturnStatus() {
  const status = _qbReturnStatus;
  _qbReturnStatus = null;
  return status;
}

// --- Push error classification --------------------------------------------

// Map an ApiError thrown by a …/push call to how the push card should react. The
// push handler returns 409 { reconnectRequired:true } when QuickBooks needs
// reconnecting and 502 { retryable:true } for a transient QuickBooks/API failure;
// anything else is a generic error. In every case the local document was left
// unchanged (Req 7.6), so the electrician can safely retry.
//   kind 'reconnect' → prompt to reconnect QuickBooks in Settings (Req 7.6).
//   kind 'retry'     → transient failure, offer a straight retry.
//   kind 'error'     → other failure, show the message.
export function pushErrorInfo(err) {
  const status = err && err.status;
  const body = (err && err.body) || {};
  if (status === 409 || body.reconnectRequired) {
    return {
      kind: 'reconnect',
      message: body.error || 'QuickBooks needs reconnecting before this can be pushed.',
    };
  }
  if (status === 502 || body.retryable) {
    return {
      kind: 'retry',
      message: body.error || 'The QuickBooks push failed. Nothing was changed — try again.',
      detail: body.detail || null,
    };
  }
  return {
    kind: 'error',
    message: (body && body.error) || (err && err.message) || 'Something went wrong. Please try again.',
  };
}

// Human label for the QuickBooks environment returned by /status + /connect
// (sandbox for the `app` stage, production for `prod`).
export function environmentLabel(environment) {
  if (environment === 'production' || environment === 'prod') return 'Production';
  if (environment === 'sandbox') return 'Sandbox';
  return environment ? String(environment) : 'Sandbox';
}

// --- Draft summary text (pure) ---------------------------------------------

function countWord(n, singular, plural) {
  return `${n} ${n === 1 ? singular : plural || `${singular}s`}`;
}

// "3 lines drafted · 1 needs your input" — from POST /quotes/draft summary
// { lineCount, pricedCount, flaggedCount }.
export function quoteDraftSummary(summary = {}) {
  const { lineCount = 0, flaggedCount = 0 } = summary || {};
  const parts = [`${countWord(lineCount, 'line')} drafted`];
  if (flaggedCount > 0) parts.push(`${flaggedCount} need${flaggedCount === 1 ? 's' : ''} your input`);
  return parts.join(' · ');
}

// "5 lines · 1 added · 1 removed · 1 needs your input" — from POST /invoices/draft
// summary { lineCount, changeCounts:{added,removed,changed,unchanged}, flaggedCount }.
export function invoiceDraftSummary(summary = {}) {
  const s = summary || {};
  const cc = s.changeCounts || {};
  const parts = [countWord(s.lineCount || 0, 'line')];
  if (cc.added) parts.push(`${cc.added} added`);
  if (cc.removed) parts.push(`${cc.removed} removed`);
  if (cc.changed) parts.push(`${cc.changed} changed`);
  if (s.flaggedCount) parts.push(`${s.flaggedCount} need${s.flaggedCount === 1 ? 's' : ''} your input`);
  return parts.join(' · ');
}

// --- Draft line → editor mapping (pure) ------------------------------------

export const CHANGE_STATUS_LABELS = {
  added: 'Added',
  removed: 'Removed',
  changed: 'Changed',
  unchanged: 'Unchanged',
};

export const RESOLUTION_LABELS = { open: 'Open', ack: 'Acknowledged', dismissed: 'Dismissed' };

// Map an API draft line (as returned on the persisted draft quote/invoice) to the
// initial data for one editor row. An unpriced line comes back priced at 0 with
// `unpriced: true` (the server never invents a price — Req 4.5); we blank its
// price field so the electrician must complete it rather than confirm a £0 line.
// The AI provenance/flag/change metadata rides along under `__meta` for the row's
// annotations; read() ignores it and returns only the editable figures.
export function draftLineToEditor(line = {}) {
  const l = line || {};
  return {
    description: l.description || '',
    quantity: l.quantity == null ? '' : l.quantity,
    unitPrice: l.unpriced ? '' : (l.unitPrice == null ? '' : l.unitPrice),
    __meta: {
      flagged: !!l.flagged,
      flagReason: l.flagReason || null,
      unpriced: !!l.unpriced,
      confidence: l.confidence || null,
      priceSource: l.priceSource || null,
      changeStatus: l.changeStatus || null,
      changeReason: l.changeReason || null,
      unit: l.unit || null,
    },
  };
}

// --- Badges / chips --------------------------------------------------------

// Guideline severity: high / medium / advisory (api/lib/flags FLAG_SEVERITIES).
export function severityBadge(severity) {
  const s = String(severity || 'advisory');
  return h('span', { class: `badge severity-${s}`, text: s });
}

// Flag resolution: open / ack / dismissed (api/lib/flags FLAG_RESOLUTIONS).
export function resolutionBadge(resolution) {
  const r = String(resolution || 'open');
  return h('span', { class: `badge resolution-${r}`, text: RESOLUTION_LABELS[r] || r });
}

// A line change status relative to the accepted quote (invoice drafts, Req 6.2).
export function changeChip(status) {
  const s = String(status || 'unchanged');
  return h('span', { class: `chip chip-change chip-${s}`, text: CHANGE_STATUS_LABELS[s] || s });
}

// A small labelled chip.
export function chip(text, variant) {
  return h('span', { class: `chip${variant ? ` chip-${variant}` : ''}`, text });
}

// --- Decision-support disclaimer (Req 5.5 / NFR6.3) ------------------------

// AI guideline output must always be labelled decision support, never a
// compliance guarantee. Used when the review itself doesn't carry one.
export const DECISION_SUPPORT_DISCLAIMER =
  'Guideline review is decision support only, not a compliance guarantee. You, ' +
  'the qualified electrician, remain responsible for design, installation, ' +
  'inspection, testing, Building Control notification, and issuing the correct ' +
  'certificate.';

export function disclaimerNote(text) {
  return h('p', { class: 'disclaimer', text: text || DECISION_SUPPORT_DISCLAIMER });
}

// --- Segmented mode toggle (Manual | AI draft) -----------------------------

// options: [{ value, label }]. Calls onSelect(value) on tap and moves the active
// marker. Returns the wrapper element.
export function modeToggle(options, active, onSelect) {
  const btns = options.map((o) => {
    const b = h(
      'button',
      { class: `seg-btn${o.value === active ? ' is-active' : ''}`, type: 'button' },
      [o.label],
    );
    b.addEventListener('click', () => {
      for (const el of wrap.children) el.classList.toggle('is-active', el === b);
      onSelect(o.value);
    });
    return b;
  });
  const wrap = h('div', { class: 'segmented', role: 'tablist' }, btns);
  return wrap;
}

// --- AI notice banner (aiUnavailable fallback) -----------------------------

// Shown when the draft endpoint reports `aiUnavailable: true` — the app still
// produced a usable (empty quote / quote-derived invoice) draft, it just wasn't
// AI-filled. Keeps quoting/invoicing unblocked (Req 5.7 posture).
export function aiUnavailableNotice(message) {
  return h('div', { class: 'ai-notice' }, [
    h('span', { class: 'ai-notice-icon', 'aria-hidden': 'true', text: '⚠' }),
    h('p', {
      class: 'ai-notice-msg',
      text:
        message ||
        'AI drafting is unavailable right now — a draft has been prepared for you to complete manually.',
    }),
  ]);
}

// --- Draft line editor -----------------------------------------------------

// Like linesEditor, but each row can show AI annotations above the editable
// fields: a flag chip + reason for lines the AI could not confidently price or
// wants reviewed (Req 4.2), and (for invoices) a change chip + reason relating
// the line to the accepted quote (Req 6.2). Flagged rows are visually marked.
// The editable fields and read() contract are identical to linesEditor, so saving
// submits plain { description, quantity, unitPrice } lines (the server recomputes
// every total — NFR6.2). Returns { el, lines, add }.
export function draftLinesEditor({ initial = [], showChange = false } = {}) {
  const subtotalEl = h('span', { class: 'lines-subtotal-value' });

  let list;
  const recompute = () => {
    if (!list) return;
    const sum = list.values().reduce((acc, l) => acc + l.quantity * l.unitPrice, 0);
    subtotalEl.textContent = moneyFromPounds(sum);
  };

  const makeRow = (data, remove, notify) => {
    const meta = data.__meta || {};
    const desc = makeInput({ type: 'text', value: data.description || '', placeholder: 'Description' });
    desc.classList.add('line-desc');
    const qty = makeInput({
      type: 'number', value: data.quantity ?? '', placeholder: 'Qty', inputmode: 'decimal', step: 'any', min: '0',
    });
    qty.classList.add('line-qty');
    const price = makeInput({
      type: 'number', value: data.unitPrice ?? '', placeholder: 'Unit £', inputmode: 'decimal', step: 'any',
    });
    price.classList.add('line-price');
    const total = h('span', { class: 'line-total' });

    const update = () => {
      total.textContent = moneyFromPounds(toNum(qty.value) * toNum(price.value));
      notify();
    };
    desc.addEventListener('input', notify);
    qty.addEventListener('input', update);
    price.addEventListener('input', update);

    // Annotations (read-only) built from the AI metadata.
    const chips = [];
    if (showChange && meta.changeStatus && meta.changeStatus !== 'unchanged') {
      chips.push(changeChip(meta.changeStatus));
    }
    if (meta.flagged) chips.push(chip(meta.unpriced ? 'Needs your price' : 'Review', 'flag'));
    if (meta.confidence === 'low' && !meta.unpriced) chips.push(chip('Low confidence', 'warn'));
    if (meta.priceSource === 'rate_card') chips.push(chip('From rate card'));
    if (meta.priceSource === 'quote') chips.push(chip('From quote'));

    const notes = [];
    if (chips.length) notes.push(h('div', { class: 'line-chips' }, chips));
    const reason = meta.flagReason || meta.changeReason;
    if (reason) notes.push(h('p', { class: 'line-note', text: reason }));

    const removeBtn = h('button', { class: 'icon-remove', type: 'button', 'aria-label': 'Remove line', onclick: remove }, ['×']);
    const rowClass = ['line-row', meta.flagged ? 'line-flagged' : '', showChange && meta.changeStatus === 'added' ? 'line-added' : ''].filter(Boolean).join(' ');
    const el = h('div', { class: rowClass }, [
      ...notes,
      desc,
      h('div', { class: 'line-nums' }, [qty, price, total]),
      removeBtn,
    ]);
    update();

    const read = () => {
      const description = desc.value.trim();
      if (!description) return null; // skip blank rows on submit
      return { description, quantity: toNum(qty.value), unitPrice: toNum(price.value) };
    };
    return { el, read };
  };

  list = repeatableList({
    initial: initial.length ? initial : [{}],
    makeRow,
    addLabel: 'Add line',
    onChange: recompute,
  });
  recompute();

  const el = h('div', { class: 'lines-editor' }, [
    h('div', { class: 'lines-head' }, [
      h('span', { text: 'Item' }),
      h('span', { text: 'Qty · Unit £ · Total' }),
    ]),
    list.el,
    h('div', { class: 'lines-subtotal' }, [
      h('span', { class: 'muted', text: 'Subtotal (preview)' }),
      subtotalEl,
    ]),
  ]);

  return { el, lines: () => list.values(), add: (data) => list.add(data) };
}

// Read-only card for invoice lines the AI reconciled as REMOVED (quoted but not
// done). They're surfaced so the electrician can confirm the omission, but are
// NEVER billed — the server already excluded them from the invoice total
// (Req 6.2). Returns null when there are none.
export function removedLinesCard(removedLines) {
  const lines = (removedLines || []).filter(Boolean);
  if (!lines.length) return null;
  const rows = lines.map((l) =>
    h('div', { class: 'removed-line' }, [
      h('div', { class: 'removed-line-main' }, [
        h('span', { class: 'removed-line-desc', text: l.description }),
        l.changeReason ? h('p', { class: 'line-note', text: l.changeReason }) : null,
      ]),
      h('span', { class: 'removed-line-amount', text: `${l.quantity} × ${moneyFromPounds(l.unitPrice)}` }),
    ]),
  );
  return card(
    [muted('Quoted but not done — shown for reference, excluded from the invoice total.'), ...rows],
    { title: 'Removed lines' },
  );
}
